import { z } from "zod";
import {
  EnrichedUserDataIdPublicSchema,
  UserEnrichmentRequestIdPublicSchema,
} from "./branded-id.schemas.js";
import { PersonalLinkedInUrlSchema } from "./linkedin-url.schemas.js";

export const enrichedEmploymentSchema = z.object({
  companyName: z.string().nullable(),
  companyUrl: z.string().nullable(),
  linkedInOrgId: z.number().nullable(),
  linkedInCompanyUrl: z.string().nullable(),
  companyId: z.string().nullable(),
  title: z.string().nullable(),
  seniority: z.string().nullable(),
  isCurrent: z.boolean(),
  locality: z.string().nullable(),
  summary: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
});

export const enrichedEducationSchema = z.object({
  schoolName: z.string(),
  degree: z.string().nullable(),
  fieldOfStudy: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  grade: z.string().nullable(),
  activities: z.string().nullable(),
});

export const enrichedLanguageSchema = z.object({
  language: z.string(),
  proficiency: z.string().nullable(),
});

export const enrichedProjectSchema = z.object({
  title: z.string(),
  summary: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  url: z.string().nullable(),
});

export const enrichedCertificationSchema = z.object({
  name: z.string(),
  authority: z.string().nullable(),
  licenseNumber: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  url: z.string().nullable(),
});

export const enrichedAwardSchema = z.object({
  title: z.string(),
  issuer: z.string().nullable(),
  issueDate: z.string().nullable(),
  description: z.string().nullable(),
});

export const enrichedPublicationSchema = z.object({
  title: z.string(),
  publisher: z.string().nullable(),
  publishDate: z.string().nullable(),
  description: z.string().nullable(),
  url: z.string().nullable(),
});

export const enrichedPatentSchema = z.object({
  title: z.string(),
  patentNumber: z.string().nullable(),
  issueDate: z.string().nullable(),
  description: z.string().nullable(),
  url: z.string().nullable(),
});

export const enrichedCourseSchema = z.object({
  name: z.string(),
  number: z.string().nullable(),
  associatedWith: z.string().nullable(),
});

export const enrichedVolunteeringSchema = z.object({
  organization: z.string(),
  role: z.string().nullable(),
  cause: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  description: z.string().nullable(),
});

export type EnrichedEmployment = z.infer<typeof enrichedEmploymentSchema>;
export type EnrichedEducation = z.infer<typeof enrichedEducationSchema>;
export type EnrichedLanguage = z.infer<typeof enrichedLanguageSchema>;
export type EnrichedProject = z.infer<typeof enrichedProjectSchema>;
export type EnrichedCertification = z.infer<typeof enrichedCertificationSchema>;
export type EnrichedAward = z.infer<typeof enrichedAwardSchema>;
export type EnrichedPublication = z.infer<typeof enrichedPublicationSchema>;
export type EnrichedPatent = z.infer<typeof enrichedPatentSchema>;
export type EnrichedCourse = z.infer<typeof enrichedCourseSchema>;
export type EnrichedVolunteering = z.infer<typeof enrichedVolunteeringSchema>;

/**
 * Domain enriched-user-data model. Single source that the backend entity type
 * extends (adding storage-only fields such as the Mongo id, raw provider
 * payload, external id, and request input) and that the admin transport view
 * projects via `.pick`, so entity, backend type, and view cannot drift apart.
 */
const _enrichedUserDataSchema = z.object({
  idPublic: EnrichedUserDataIdPublicSchema,

  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  fullName: z.string().nullable(),
  headline: z.string().nullable(),
  bio: z.string().nullable(),

  linkedInUrl: PersonalLinkedInUrlSchema.nullable(),
  linkedInSlug: z.string().nullable(),
  linkedInProfileId: z.number().nullable(),
  photoUrl: z.string().nullable(),
  location: z.string().nullable(),
  country: z.string().nullable(),
  connectionsCount: z.number().nullable(),
  followersCount: z.number().nullable(),
  currentTitle: z.string().nullable(),
  currentOrganization: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  industries: z.array(z.string()),
  skills: z.array(z.string()),

  employments: z.array(enrichedEmploymentSchema),
  currentEmployments: z.array(enrichedEmploymentSchema),
  latestEmployment: enrichedEmploymentSchema.nullable(),
  education: z.array(enrichedEducationSchema),
  languages: z.array(enrichedLanguageSchema),
  projects: z.array(enrichedProjectSchema),
  certifications: z.array(enrichedCertificationSchema),
  awards: z.array(enrichedAwardSchema),
  publications: z.array(enrichedPublicationSchema),
  patents: z.array(enrichedPatentSchema),
  courses: z.array(enrichedCourseSchema),
  volunteering: z.array(enrichedVolunteeringSchema),

  enrichmentSource: z.string(),
  enrichmentConfidence: z.number(),
  enrichedAt: z.coerce.date(),
  enrichmentRequestIdPublic: UserEnrichmentRequestIdPublicSchema.nullable(),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const EnrichedUserDataSchema = _enrichedUserDataSchema.readonly();

export type EnrichedUserData = z.infer<typeof EnrichedUserDataSchema>;
